import React from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { HEAT_PALETTE, LANE_CONFIG } from "./boardConfig";
import { navigateToLeadDetail } from "../../utils/leadDetailNavigation";

const CHANNEL_ORDER = ["sms", "email"];
const CHANNEL_SENT_LABEL = { sms: "SMS sent", email: "Email sent" };

function formatCalendarSentChip(iso) {
  if (!iso) return null;
  const sent = new Date(iso);
  if (Number.isNaN(sent.getTime())) return null;

  const now = new Date();
  const sentDay = new Date(sent.getFullYear(), sent.getMonth(), sent.getDate());
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((todayStart - sentDay) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return "Sent today";
  if (diffDays === 1) return "Sent yesterday";
  return `Sent ${sent.toLocaleDateString(undefined, { dateStyle: "medium" })}`;
}

function HeatChip({ heatLevel }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (!heatLevel) return null;

  const palette = HEAT_PALETTE[heatLevel]?.[isDark ? "dark" : "light"];
  if (!palette) return null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        bgcolor: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        lineHeight: 1.6,
      }}
    >
      {heatLevel === "yellow" ? "Warm" : heatLevel === "orange" ? "Hot" : "Critical"}
    </Box>
  );
}

function InfoRow({ label, value, valueSlot }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ minHeight: 22 }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, mr: 1 }}>
        {label}
      </Typography>
      {valueSlot ?? (
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.primary"
          sx={{ textAlign: "right", wordBreak: "break-word" }}
        >
          {value || "—"}
        </Typography>
      )}
    </Stack>
  );
}

export default function BoardCard({ card }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const { follow_up, owner, territory, activity, badges, flags, lane, automation } = card;
  const laneConfig = LANE_CONFIG[lane] ?? {};
  const chipBg = isDark ? (laneConfig.chipBgDark ?? "rgba(255,255,255,0.06)") : (laneConfig.chipBgLight ?? "rgba(0,0,0,0.04)");
  const chipText = isDark ? (laneConfig.chipTextDark ?? "rgba(255,255,255,0.75)") : (laneConfig.chipTextLight ?? "rgba(0,0,0,0.65)");
  const chipBorder = isDark ? (laneConfig.borderDark ?? "rgba(255,255,255,0.14)") : (laneConfig.borderLight ?? "rgba(0,0,0,0.14)");
  const laneChipSx = {
    height: 23,
    fontSize: "0.66rem",
    fontWeight: 700,
    borderRadius: "8px",
    borderColor: chipBorder,
    color: chipText,
    bgcolor: chipBg,
    "& .MuiChip-label": { px: 1 },
  };
  const showHeat = flags.is_overdue;

  const handleOpen = (e) => {
    e.stopPropagation();
    navigateToLeadDetail(navigate, card.id, {
      pathname: "/follow-up-intelligence",
      label: "Follow-Up Intel",
    });
  };

  return (
    <Box
      onClick={handleOpen}
      sx={{
        borderRadius: "18px",
        border: `1px solid ${
          flags?.is_overdue
            ? isDark ? "rgba(248,113,113,0.26)" : "rgba(239,68,68,0.26)"
            : isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)"
        }`,
        bgcolor: isDark ? "rgba(30,41,59,0.95)" : "#ffffff",
        p: 2,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.32), 0 1px 2px rgba(0,0,0,0.24)"
          : "0 2px 8px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.05)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: isDark
            ? "0 12px 30px rgba(0,0,0,0.42)"
            : "0 12px 30px rgba(0,0,0,0.12)",
        },
        ...(flags?.is_overdue && {
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.32), 0 0 0 1px rgba(248,113,113,0.14)"
            : "0 2px 8px rgba(15,23,42,0.08), 0 0 0 1px rgba(239,68,68,0.12)",
        }),
      }}
    >
      {/* Urgency accent bar — overdue cards only */}
      {flags?.is_overdue && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: "linear-gradient(180deg, #ef4444 0%, #dc2626 100%)",
          }}
        />
      )}

      {/* Header: name + rep */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.92rem",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {card.lead_name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.4 }}>
            <PersonOutlineIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {owner?.name || "Unassigned"}
            </Typography>
          </Stack>
          {showHeat && follow_up?.display_text && (
            <Typography
              sx={{
                mt: 0.55,
                fontSize: "0.72rem",
                fontWeight: 800,
                color: "error.main",
              }}
            >
              {follow_up.display_text}
            </Typography>
          )}
        </Box>

        {showHeat && follow_up?.heat_level && (
          <Box sx={{ flexShrink: 0, mt: 0.2 }}>
            <HeatChip heatLevel={follow_up.heat_level} />
          </Box>
        )}
      </Stack>

      {/* Info panel */}
      <Box
        sx={{
          mt: 1.4,
          borderRadius: "12px",
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)"}`,
          px: 1.4,
          py: 1.2,
        }}
      >
        <Stack spacing={0.7}>
          <InfoRow label="Territory" value={territory?.name} />
          <InfoRow label="Follow-Up" value={follow_up?.display_text} />
          <InfoRow label="Last Updated" value={activity?.last_updated_text} />
        </Stack>
      </Box>

      {/* Badges */}
      {badges?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
          {badges.map((badge) => (
            <Chip
              key={badge}
              label={badge}
              size="small"
              variant="outlined"
              sx={laneChipSx}
            />
          ))}
        </Stack>
      )}

      {lane === "system_followed_up" && automation && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ mt: 1.5 }}>
          {CHANNEL_ORDER.filter((ch) => automation.channels?.includes(ch)).map((ch) => (
            <Chip
              key={ch}
              label={CHANNEL_SENT_LABEL[ch] ?? ch}
              size="small"
              variant="outlined"
              sx={laneChipSx}
            />
          ))}
          <Chip
            label={
              typeof automation.attempt_count === "number"
                ? `${automation.attempt_count} successful send${automation.attempt_count === 1 ? "" : "s"}`
                : "—"
            }
            size="small"
            variant="outlined"
            title="Successful automation sends logged for this follow-up (not retry attempts)."
            sx={laneChipSx}
          />
          <Chip
            label={formatCalendarSentChip(automation.last_sent_at) ?? "—"}
            size="small"
            variant="outlined"
            sx={laneChipSx}
          />
        </Stack>
      )}

      {/* CTA */}
      <Button
        fullWidth
        variant="contained"
        size="small"
        onClick={handleOpen}
        sx={{
          mt: 1.6,
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 800,
          fontSize: "0.78rem",
          py: 0.9,
          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          color: "#fff",
          boxShadow: "0 8px 20px rgba(37,99,235,0.28)",
          "&:hover": {
            background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
            boxShadow: "0 12px 24px rgba(37,99,235,0.34)",
          },
        }}
      >
        Open Lead
      </Button>
    </Box>
  );
}
